<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useGlobalStore } from '@stores/global'
import { useAuthStore } from '@stores/auth'
import Loading from '@sections/global/Loading.vue'
import Button from '@components/forms/Button.vue'
import EventCard from '@components/global/EventCard.vue'
import EventSection from '@components/event/EventSection.vue'
import markdownit from 'markdown-it'
import markdownItAttrs from 'markdown-it-attrs'

const md = markdownit()
const global = useGlobalStore()
const auth = useAuthStore()
const route = useRoute()
const loading = ref(false)
const event = ref({})
const requestEvent = ref(null)
const slug = computed(() => route.params.slug)
const currentTime = ref(new Date())

md.use(markdownItAttrs, { allowedAttributes: ['class', 'target'] })

const prizing = computed(() => {
	return event.value.prizing ? md.render(event.value.prizing) : null
})
const rules = computed(() => {
	return event.value.rules ? md.render(event.value.rules) : null
})
const isDateTimePast = (dateTime) => {
	if (!dateTime) return false

	let eventDate
	if (typeof dateTime === 'string') {
		eventDate = new Date(dateTime)
	} else if (dateTime instanceof Date) {
		eventDate = dateTime
	} else {
		return false
	}
	if (isNaN(eventDate.getTime())) {
		return false
	}
	return eventDate < currentTime.value
}

const registrationsClosed = computed(() => (event.value.registrations_closed_at ? isDateTimePast(event.value.registrations_closed_at) : isDateTimePast(event.value.finish_at)))

let timeInterval = null

onMounted(() => {
	timeInterval = setInterval(() => {
		currentTime.value = new Date()
	}, 1000)
})

onUnmounted(() => {
	if (timeInterval) {
		clearInterval(timeInterval)
	}
})

function cancelEvent() {
	if (requestEvent.value) requestEvent.value.cancel()
}
function getEvent() {
	loading.value = true
	event.value = {}
	cancelEvent()
	let axiosSource = axios.CancelToken.source()
	requestEvent.value = { cancel: axiosSource.cancel }
	axios
		.get(`/api/event/${route.params.slug}`, {
			cancelToken: axiosSource.token,
		})
		.then((a) => {
			loading.value = false
			event.value = a.data
		})
		.catch((e) => {})
}
getEvent()

watch(slug, async () => {
	getEvent()
})
</script>

<template>
	<Loading v-if="loading" />
	<section class="relative bg-linear-to-bl from-logi-grey-600 via-black to-black" v-else>
		<div class="clip absolute -left-10 -top-10 h-80 w-60 bg-logi-blue-500 lg:left-0 lg:top-0" :style="[event.accent_hex ? { backgroundColor: event.accent_hex } : null]" />
		<div class="container">
			<div class="flex flex-col lg:flex-row lg:gap-10">
				<div class="mt-16 w-full py-4 lg:py-12">
					<div class="sticky top-28 flex flex-col gap-3">
						<router-link class="mb-1 self-end text-sm uppercase text-logi-blue-500 transition-colors duration-200 hover:text-white lg:hidden" :style="[event.accent_hex ? { color: event.accent_hex } : null]" :to="{ name: 'index' }">
							<i class="fas fa-arrow-left mr-2" />
							Back to Events Page
						</router-link>
						<EventCard :event="event" :accent="event.accent_hex" />
						<div class="flex gap-3">
							<Button class="solid grey" icon="fab fa-discord" href="https://discord.com/invite/LogitechG">Discord</Button>
							<template v-if="!auth.isAuthed">
								<Button class="solid secondary grow" icon="fas fa-arrow-right" disabled>Login to Register</Button>
							</template>
							<template v-else-if="auth.user?.events?.filter((registeredEvent) => registeredEvent.slug == event.slug).length > 0">
								<Button class="solid secondary grow" icon="far fa-arrow-up-right" :accent="event.accent_hex" :to="{ name: 'account.events' }">My Events</Button>
							</template>
							<template v-else-if="registrationsClosed">
								<Button class="solid secondary grow" icon="fas fa-calendar-circle-exclamation" disabled>
									<span class="mr-1.5 hidden xs:block">Registrations</span>
									Closed
								</Button>
							</template>
							<template v-else>
								<Button class="solid secondary grow" icon="far fa-arrow-right-to-bracket" @click="global.setModal(['Register', event])" :accent="event.accent_hex">
									<div class="flex gap-1">
										Register
										<template class="hidden xs:block">For Event</template>
									</div>
								</Button>
							</template>
						</div>
					</div>
				</div>
				<div class="hidden border-r border-white/10 lg:block"></div>
				<div class="flex w-full flex-col gap-8 pb-10 lg:mt-28 lg:gap-10">
					<EventSection class="!pb-2 lg:!pb-7">
						<router-link class="hidden text-sm uppercase text-logi-blue-500 transition-colors duration-200 hover:text-white lg:flex" :style="[event.accent_hex ? { color: event.accent_hex } : null]" :to="{ name: 'index' }">
							<i class="fas fa-arrow-left mr-2" />
							Back to Events Page
						</router-link>
					</EventSection>
					<EventSection>
						<p class="text-sm" v-if="event.game?.name">{{ event.game.name }}</p>
						<h2>{{ event.name }}</h2>
						<p class="text-white/70" v-if="event.description">{{ event.description }}</p>
						<div class="grid grid-cols-3 gap-2">
							<div class="h-16 rounded-md bg-logi-grey-500 p-4 lg:p-2 xl:h-20 xl:p-5" v-for="partner in event.partners">
								<img :src="partner.logo" :alt="partner.name" class="h-full w-full object-contain" />
							</div>
						</div>
					</EventSection>
					<EventSection v-if="prizing">
						<div class="markdown" v-html="prizing"></div>
					</EventSection>
					<EventSection v-if="rules">
						<h6>Event Rules</h6>
						<div class="markdown" v-html="rules"></div>
					</EventSection>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped>
	@reference "../../css/app.css";

.clip {
	clip-path: polygon(0 0, 0% 100%, 100% 0);
}

:deep(.markdown h1) {
	@apply mb-2 text-lg font-bold lg:text-xl;
}
:deep(.markdown h2) {
	@apply mb-2 text-base font-bold lg:text-lg;
}
:deep(.markdown p) {
	@apply mb-4 text-white/70;
}
:deep(.markdown a) {
	@apply text-logi-blue-500;
}
:deep(.markdown ul) {
	@apply list-disc;
}
:deep(.markdown li) {
	@apply text-white/70;
}
</style>
